import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@material-tailwind/react";
import {
  FaBookOpen,
  FaCheck,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa6";
import { FaInfoCircle } from "react-icons/fa";
import Header from "../components/Header";
import api from "../utils/axiosConfig";
import { CourseListItem } from "../utils/entities/course";
import {
  CoursePageShell,
  CourseCoverThumb,
  CourseSectionProgress,
  CourseListSkeleton,
} from "../components/courses/CourseUi";

type CourseScope =
  | "all_companies"
  | "advogado"
  | "company"
  | "optional";

const ctaLabel = (status: CourseListItem["status"]) => {
  if (status === "completed") return "Abrir";
  if (status === "failed") return "Ver curso";
  if (status === "in_progress") return "Continuar";
  return "Iniciar";
};

const getCourseScope = (course: CourseListItem): CourseScope => {
  if (course.isRequiredForAdvogadoHearings) return "advogado";
  if (course.companyId) return "company";
  if (course.isRequiredForHearings) return "all_companies";
  return "optional";
};

/** Cursos que liberam a agenda geral (não inclui só-Advogado). */
const isAgendaBlockingCourse = (course: CourseListItem) => {
  const scope = getCourseScope(course);
  return scope === "all_companies" || scope === "company";
};

const isPending = (course: CourseListItem) => course.status !== "completed";

const sortPendingFirst = (items: CourseListItem[]) =>
  [...items].sort((a, b) => {
    const aDone = a.status === "completed" ? 1 : 0;
    const bDone = b.status === "completed" ? 1 : 0;
    if (aDone !== bDone) return aDone - bDone;
    if (a.status === "failed" && b.status !== "failed") return -1;
    if (b.status === "failed" && a.status !== "failed") return 1;
    if (a.status === "in_progress" && b.status !== "in_progress") return -1;
    if (b.status === "in_progress" && a.status !== "in_progress") return 1;
    return 0;
  });

const scopeMeta: Record<CourseScope, { title: string; hint: string }> = {
  all_companies: {
    title: "Para liberar sua agenda",
    hint: "Obrigatórios para atuar em audiências de qualquer empresa.",
  },
  advogado: {
    title: "Só para audiências como advogado",
    hint: "Obrigatórios apenas para advogados.",
  },
  company: {
    title: "Para empresas específicas",
    hint: "Obrigatórios apenas para audiências da empresa indicada em cada curso.",
  },
  optional: {
    title: "Formação opcional",
    hint: "Não liberam nem bloqueiam a agenda — faça quando quiser se aprofundar.",
  },
};

const SECTION_ORDER: CourseScope[] = [
  "all_companies",
  "advogado",
  "company",
  "optional",
];

const sectionProgressLabel = (
  scope: CourseScope,
  items: CourseListItem[]
) => {
  const pendingItems = items.filter(isPending);
  const pending = pendingItems.length;
  const total = items.length;
  const completed = total - pending;

  if (pending > 0) {
    const onlyOneInProgress =
      pending === 1 && pendingItems[0].status === "in_progress";
    if (onlyOneInProgress) return "Em andamento";
    if (scope === "optional") {
      return `${completed} de ${total} concluído${completed === 1 ? "" : "s"}`;
    }
    return pending === 1 ? "1 pendente" : `${pending} pendentes`;
  }

  return total === 1 ? "1 de 1 concluído" : `${total} de ${total} concluídos`;
};

const CoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openOverride, setOpenOverride] = useState<
    Partial<Record<CourseScope, boolean>>
  >({});

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const { data } = await api.get<CourseListItem[]>("agent/courses");
        setCourses(data);
      } catch {
        setError("Não foi possível carregar os cursos.");
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const stats = useMemo(() => {
    const completed = courses.filter((c) => c.status === "completed").length;
    const inProgress = courses.filter((c) => c.status === "in_progress").length;
    const pendingRequired = sortPendingFirst(
      courses.filter((c) => isAgendaBlockingCourse(c) && isPending(c))
    );
    return {
      total: courses.length,
      completed,
      inProgress,
      pendingRequiredCount: pendingRequired.length,
      nextPendingRequired: pendingRequired[0] ?? null,
    };
  }, [courses]);

  const grouped = useMemo(() => {
    const map: Record<CourseScope, CourseListItem[]> = {
      all_companies: [],
      advogado: [],
      company: [],
      optional: [],
    };
    for (const course of courses) {
      map[getCourseScope(course)].push(course);
    }
    for (const scope of SECTION_ORDER) {
      map[scope] = sortPendingFirst(map[scope]);
    }
    return map;
  }, [courses]);

  const defaultOpenByScope = useMemo(() => {
    const hasPendingRequired = stats.pendingRequiredCount > 0;
    const scopesWithItems = SECTION_ORDER.filter(
      (scope) => grouped[scope].length > 0
    );
    return {
      all_companies: grouped.all_companies.some(isPending),
      advogado: grouped.advogado.some(isPending),
      company: grouped.company.some(isPending),
      optional:
        !hasPendingRequired &&
        grouped.optional.length > 0 &&
        scopesWithItems.length === 1,
    } satisfies Record<CourseScope, boolean>;
  }, [grouped, stats.pendingRequiredCount]);

  const isSectionOpen = (scope: CourseScope) => {
    if (Object.prototype.hasOwnProperty.call(openOverride, scope)) {
      return openOverride[scope] === true;
    }
    return defaultOpenByScope[scope];
  };

  const toggleSection = (scope: CourseScope) => {
    setOpenOverride((prev) => ({
      ...prev,
      [scope]: !isSectionOpen(scope),
    }));
  };

  const goToCourse = (course: CourseListItem) => {
    const scope = getCourseScope(course);
    setOpenOverride((prev) => ({ ...prev, [scope]: true }));
    navigate(`/courses/${course.id}`);
  };

  const renderCourseCard = (course: CourseListItem) => {
    const scope = getCourseScope(course);
    const pending = isAgendaBlockingCourse(course) && isPending(course);
    return (
      <li key={course.id}>
        <button
          type="button"
          onClick={() => goToCourse(course)}
          className={`w-full text-left px-4 py-3.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-preppoBlue/30 ${
            pending
              ? "bg-blue-50/60 border-preppoBlue/25 border-l-[4px] border-l-preppoBlue shadow-sm hover:shadow-md"
              : "border-slate-200 border-l-[4px] border-l-transparent hover:bg-slate-50/80 hover:shadow-md hover:border-slate-300"
          }`}
        >
          <div className="flex items-center gap-4">
            <CourseCoverThumb
              coverUrl={course.coverUrl}
              completed={course.status === "completed"}
            />
            <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="font-semibold text-slate-900 text-lg tracking-tight">
                    {course.title}
                  </h3>
                  {course.status === "in_progress" && (
                    <span className="text-xs font-medium text-preppoBlue">
                      Em andamento
                    </span>
                  )}
                  {course.status === "failed" && (
                    <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200">
                      Reprovado
                    </span>
                  )}
                </div>
                {(scope === "company" ||
                  (scope === "advogado" && course.companyId)) && (
                  <p className="text-sm text-slate-500 mb-1">
                    {course.companyName || "Empresa específica"}
                  </p>
                )}
                {course.description && (
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {course.description}
                  </p>
                )}
              </div>

              <span
                className={`inline-flex items-center gap-2 self-start sm:self-center shrink-0 text-sm font-semibold transition-all ${
                  pending
                    ? "rounded-lg bg-preppoBlue px-4 py-2 text-white shadow-sm"
                    : "text-preppoBlue"
                }`}
              >
                {ctaLabel(course.status)}
                <FaChevronRight
                  className={`text-xs ${
                    pending ? "opacity-90" : "opacity-60"
                  }`}
                />
              </span>
            </div>
          </div>
        </button>
      </li>
    );
  };

  const renderSection = (scope: CourseScope, items: CourseListItem[]) => {
    const meta = scopeMeta[scope];
    const open = isSectionOpen(scope);
    const progressLabel = sectionProgressLabel(scope, items);
    const completedCount = items.filter((c) => c.status === "completed").length;
    const allDone = items.every((c) => c.status === "completed");

    return (
      <section
        key={scope}
        className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
      >
        <button
          type="button"
          onClick={() => toggleSection(scope)}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50/60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-preppoBlue/30 transition-colors"
          aria-expanded={open}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-base font-bold text-preppoBlue tracking-tight truncate">
                {meta.title}
              </h2>
              <Tooltip
                content={
                  <span className="block max-w-[260px] text-xs">{meta.hint}</span>
                }
              >
                <span
                  className="cursor-help shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <FaInfoCircle className="text-slate-300 text-xs" />
                </span>
              </Tooltip>
              {allDone ? (
                <FaCheck
                  className="text-emerald-600 text-xs shrink-0"
                  aria-hidden
                />
              ) : null}
            </div>
            {!allDone && (
              <CourseSectionProgress
                completed={completedCount}
                total={items.length}
                label={progressLabel}
              />
            )}
          </div>
          <FaChevronDown
            className={`text-slate-500 text-sm transition-transform duration-200 ease-out shrink-0 ${
              open ? "rotate-180" : "rotate-0"
            }`}
          />
        </button>

        <div
          className={`overflow-hidden transition-all duration-[220ms] ease-out ${
            open
              ? "max-h-[2000px] opacity-100 border-t border-slate-100"
              : "max-h-0 opacity-0"
          }`}
        >
          <ul className="flex flex-col gap-4 p-4">{items.map(renderCourseCard)}</ul>
        </div>
      </section>
    );
  };

  return (
    <>
      <Header />
      <CoursePageShell>
        <header className="mb-6 bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-4 md:px-5 flex items-center gap-3">
            <div
              aria-hidden
              className="h-10 w-10 rounded-lg bg-gradient-to-br from-preppoBlue to-blue-700 text-white flex items-center justify-center shrink-0 shadow-sm"
            >
              <FaBookOpen className="text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-preppoBlue tracking-tight">
                Cursos
              </h1>
              <p className="text-sm text-slate-600 mt-0.5">
                Complete os cursos da agenda para receber audiências
              </p>
            </div>
          </div>

          <div
            className={`border-t px-4 py-3.5 md:px-5 ${
              stats.pendingRequiredCount > 0
                ? "bg-blue-50/80 border-blue-100"
                : "bg-emerald-50/60 border-emerald-100"
            }`}
          >
            {stats.pendingRequiredCount > 0 && stats.nextPendingRequired ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm text-slate-700">
                  <span className="font-semibold text-preppoBlue">
                    {stats.pendingRequiredCount === 1
                      ? "1 curso da agenda pendente"
                      : `${stats.pendingRequiredCount} cursos da agenda pendentes`}
                  </span>
                </p>
                <button
                  type="button"
                  onClick={() => goToCourse(stats.nextPendingRequired!)}
                  className="inline-flex items-center justify-center gap-1.5 self-start sm:self-center shrink-0 max-w-full rounded-lg bg-preppoBlue px-4 py-2.5 text-sm font-medium text-white hover:bg-preppoBlue/90 focus:outline-none focus:ring-2 focus:ring-preppoBlue/40 shadow-sm transition-all"
                >
                  <span className="shrink-0">
                    {ctaLabel(stats.nextPendingRequired.status)}
                    :
                  </span>
                  <span className="min-w-0 max-w-[12rem] sm:max-w-[16rem] truncate">
                    {stats.nextPendingRequired.title}
                  </span>
                  <FaChevronRight className="text-[10px] opacity-80 shrink-0" />
                </button>
              </div>
            ) : courses.length > 0 && !loading ? (
              <div className="flex items-start gap-3">
                <span
                  className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm"
                  aria-hidden
                >
                  <FaCheck className="text-sm" />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-emerald-800">
                      Cursos da agenda concluídos
                    </p>
                    {grouped.advogado.length > 0 && (
                      <Tooltip
                        content={
                          <span className="block max-w-[260px] text-xs">
                            Cursos só para advogado não são necessários se você
                            não atua nessa modalidade.
                          </span>
                        }
                      >
                        <span className="cursor-help">
                          <FaInfoCircle className="text-slate-300 text-xs" />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <p className="text-sm text-emerald-700/90 mt-0.5">
                    Você está apto a receber audiências
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </header>

        {loading && <CourseListSkeleton />}
        {error && <p className="text-red-600 text-sm">{error}</p>}

        {!loading && !error && courses.length === 0 && (
          <div className="flex flex-col items-center justify-center min-h-[30vh] p-8 text-center border border-dashed border-slate-200 rounded-xl bg-white">
            <h3 className="text-base font-semibold text-slate-900">
              Nenhum curso disponível
            </h3>
            <p className="text-slate-500 mt-2 text-sm">
              Quando houver cursos liberados para você, eles aparecerão aqui.
            </p>
          </div>
        )}

        {!loading && !error && courses.length > 0 && (
          <div className="flex flex-col gap-4">
            {SECTION_ORDER.map((scope) => {
              const items = grouped[scope];
              if (items.length === 0) return null;
              return renderSection(scope, items);
            })}
          </div>
        )}
      </CoursePageShell>
    </>
  );
};

export default CoursesPage;
